import { AlertCircle, Home, RefreshCw, Settings } from 'lucide-react';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@bliks-io/ui';
import { reloadBrowser } from '@/lib/browser-redirect';
import { isChunkLoadError } from '@/lib/chunk-load-recovery';
import { usePermissions } from '@/hooks/usePermissions';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

/**
 * ErrorFallback Component
 *
 * UI component for displaying error information with retry and navigation options.
 * Designed to render within the main content area while keeping navigation visible.
 *
 * @example
 * ```tsx
 * <ErrorFallback
 *   error={new Error('Something went wrong')}
 *   resetError={() => console.log('Retry')}
 * />
 * ```
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const { slug } = useParams({ strict: false });
  const { can } = usePermissions();

  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <Card className="max-w-2xl w-full border-destructive/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">{t('common.ErrorBoundary.title')}</CardTitle>
          </div>
          <CardDescription>{t('common.ErrorBoundary.description')}</CardDescription>
        </CardHeader>

        <CardContent>
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                {t('common.ErrorBoundary.viewErrorDetails')}
              </summary>
              <div className="mt-3 p-4 bg-muted rounded-lg border">
                <p className="text-sm font-semibold text-destructive mb-2">
                  {error.name}: {error.message}
                </p>
                {error.stack && (
                  <pre className="text-xs text-muted-foreground overflow-auto max-h-[200px] whitespace-pre-wrap">
                    {error.stack}
                  </pre>
                )}
              </div>
            </details>
          )}
        </CardContent>

        <CardFooter className="flex flex-wrap gap-2">
          <Button
            onClick={isChunkLoadError(error) ? reloadBrowser : resetError}
            variant="primary"
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t('common.ErrorBoundary.tryAgain')}
          </Button>

          {slug && can('OrgSettings', 'read') && (
            <Link to="/$slug/organization-settings/settings" params={{ slug }} onClick={resetError}>
              <Button variant="outline" className="gap-2">
                <Settings className="h-4 w-4" />
                {t('common.ErrorBoundary.goToSettings')}
              </Button>
            </Link>
          )}

          <Link to="/" onClick={resetError}>
            <Button variant="outline" className="gap-2">
              <Home className="h-4 w-4" />
              {t('common.ErrorBoundary.goHome')}
            </Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
